<template>
  <form
    :action="formAction"
    :method="effectiveMethod === 'get' ? 'get' : 'post'"
    class="form-horizontal vxlan-form-layout"
  >
    <input v-if="csrfToken && effectiveMethod !== 'get'" type="hidden" name="authenticity_token" :value="csrfToken">
    <input v-if="effectiveMethod !== 'post' && effectiveMethod !== 'get'" type="hidden" name="_method" :value="effectiveMethod">

    <div class="form-group row" :class="{ 'vxlan-field-invalid': !!nameError }">
      <label class="col-sm-3 control-label" for="vxlan_name">Name</label>
      <div class="col-sm-9">
        <input id="vxlan_name" v-model="name" name="vxlan[name]" type="text" autocomplete="off" class="form-control">
      </div>
    </div>
    <div v-if="nameError" class="vxlan-inline-error">{{ nameError }}</div>

    <div class="form-group row" :class="{ 'vxlan-field-invalid': !!vniError }">
      <label class="col-sm-3 control-label" for="vxlan_vni">VNI</label>
      <div class="col-sm-9">
        <input
          id="vxlan_vni"
          v-model="vni"
          name="vxlan[vni]"
          type="text"
          autocomplete="off"
          inputmode="numeric"
          :placeholder="autoVni ? 'Optional. Auto assign from 5001 - 16777214' : '5001 - 16777214'"
          class="form-control"
        >
      </div>
    </div>
    <div v-if="vniError" class="vxlan-inline-error">{{ vniError }}</div>

    <div class="form-group row">
      <label class="col-sm-3 control-label" for="vxlan_description">Description</label>
      <div class="col-sm-9">
        <input id="vxlan_description" v-model="description" name="vxlan[description]" type="text" class="form-control">
      </div>
    </div>

    <div class="form-group boolean optional vxlan-visibility-group">
      <label class="col-sm-3 control-label">Visibility</label>
      <div class="col-sm-9">
        <input type="hidden" name="vxlan[public]" value="false">
        <label class="vxlan-visibility-toggle">
          <input v-model="publicChecked" name="vxlan[public]" type="checkbox" value="true">
          <span class="vxlan-visibility-slider" aria-hidden="true"></span>
          <span class="vxlan-visibility-copy">
            <strong>Public VXLAN</strong>
            <small>Enable when this VXLAN should be available to all users.</small>
          </span>
        </label>
      </div>
    </div>

    <div class="form-actions">
      <button type="submit" class="btn btn-primary" :disabled="submitDisabled">{{ submitLabel }}</button>
    </div>
  </form>
</template>

<script setup>
import { computed, ref } from "vue"

const props = defineProps({
  formAction: {
    type: String,
    required: true
  },
  formMethod: {
    type: String,
    default: "post"
  },
  submitLabel: {
    type: String,
    default: "Save"
  },
  initialName: {
    type: String,
    default: ""
  },
  initialVni: {
    type: [String, Number],
    default: ""
  },
  initialDescription: {
    type: String,
    default: ""
  },
  initialPublic: {
    type: Boolean,
    default: false
  },
  existingVnis: {
    type: Array,
    default: () => []
  },
  autoVni: {
    type: Boolean,
    default: false
  }
})

const csrfToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content") || ""
const effectiveMethod = props.formMethod.toLowerCase()
const name = ref(props.initialName)
const vni = ref(String(props.initialVni || ""))
const description = ref(props.initialDescription)
const publicChecked = ref(!!props.initialPublic)
const initialVniValue = String(props.initialVni || "").trim()
const normalizedExistingVnis = computed(() =>
  (props.existingVnis || []).map((value) => String(value || "").trim()).filter(Boolean)
)
const normalizedName = computed(() => String(name.value || "").trim())
const normalizedVni = computed(() => String(vni.value || "").trim())

const nameError = computed(() => {
  if (!normalizedName.value) {
    return "Name is required."
  }
  return ""
})

const vniError = computed(() => {
  const value = normalizedVni.value

  if (!value && !props.autoVni) {
    return "VNI is required."
  }
  if (!value) {
    return ""
  }
  if (!/^\d+$/.test(value)) {
    return "VNI must be a number."
  }
  const parsed = parseInt(value, 10)
  if (parsed < 5001 || parsed > 16777214) {
    return "VNI must be between 5001 and 16777214."
  }
  if (value !== initialVniValue && normalizedExistingVnis.value.includes(value)) {
    return "This VNI is already in use."
  }
  return ""
})

const submitDisabled = computed(() => !!nameError.value || !!vniError.value)
</script>
